
import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { environment } from 'src/environments/environment';
import { Web3Service } from '@/services/web3.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ThemeService } from '@/services/theme.service';
import { StateService } from '@/services/state.service';
import { DataService } from '@/services/data.service';
import { TransactionTrackingService } from '@/services/transaction-tracking.service';
import { Punk } from '@/models/graph';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { TokenIdParsePipe } from '@/pipes/token-id-parse.pipe';
import { LoaderComponent } from '../icons/loader.component';
import { NotConnectedComponent } from '../not-connected/not-connected.component';

type WrapStep = 'none' | 'sentToWrapContract';

@Component({
  selector: 'app-v1-wrapper',
  standalone: true,
  imports: [
    CommonModule,
    LoaderComponent,
    LazyLoadImageModule,
    TokenIdParsePipe,
    NotConnectedComponent,
  ],
  templateUrl: './wrapper.v1.component.html',
  styleUrl: './wrapper.v1.component.scss'
})
export class WrapperV1Component implements OnInit, OnDestroy {
  environment = environment;
  owned: BehaviorSubject<Punk[] | null> = new BehaviorSubject<Punk[] | null>(null);
  loading = false;
  wrapStep: WrapStep = 'none';
  current: Punk | null = null;
  isWrapperTransactionActive = false;
  isActionBusy = false;

  ownedCount = 0;
  wrappedCount = 0;
  unwrappedCount = 0;
  wrappedPunkIds: number[] = [];

  private walletSubscription?: Subscription;

  constructor(
    public themeSvc: ThemeService,
    private web3Svc: Web3Service,
    protected dataSvc: DataService,
    protected stateSvc: StateService,
    public txTrackingSvc: TransactionTrackingService,
  ) {
    this.walletSubscription = this.stateSvc.walletAddress$.subscribe((wallet) => {
      if (!wallet) {
        this.resetOwnedState();
        return;
      }
      this.loadOwned(wallet);
    });
  }

  ngOnInit(): void {
    window.addEventListener('selectPunkForWrap', this.handleSelectPunkForWrap as EventListener);
  }

  ngOnDestroy(): void {
    window.removeEventListener('selectPunkForWrap', this.handleSelectPunkForWrap as EventListener);
    this.walletSubscription?.unsubscribe();
  }

  private handleSelectPunkForWrap = (event: CustomEvent): void => {
    if (environment.collection !== 'cryptopunks-v1') {
      return;
    }

    const { punkId } = event.detail;

    if (this.loading || this.owned.getValue() === null) {
      this.waitForOwnedAndSelectPunk(punkId);
      return;
    }

    this.selectPunkById(punkId);
  };

  private waitForOwnedAndSelectPunk(punkId: number): void {
    const check = () => {
      if (!this.loading && this.owned.getValue() !== null) {
        this.selectPunkById(punkId);
      } else {
        setTimeout(check, 500);
      }
    };

    check();
  }

  private selectPunkById(punkId: number): void {
    const owned = this.owned.getValue();
    if (!owned) {
      return;
    }

    const punk = owned.find((item) => Number(item.id) === Number(punkId));
    if (punk) {
      this.selectPunk(punk);
      return;
    }

    console.warn(`[WrapperV1Component] Punk ${punkId} not found in owned list`);
  }

  selectPunk(punk: Punk): void {
    if (this.isActionBusy) {
      return;
    }
    this.current = punk;
    this.wrapStep = 'none';
  }

  isSelected(punk: Punk): boolean {
    return this.current?.id === punk.id;
  }

  private resetOwnedState(): void {
    this.owned.next(null);
    this.loading = false;
    this.current = null;
    this.wrapStep = 'none';
    this.updateOwnedStats([]);
  }

  private loadOwned(wallet: string): void {
    this.loading = true;
    this.owned.next(null);
    this.current = null;
    this.wrapStep = 'none';

    this.dataSvc.fetchOwned(wallet).subscribe({
      next: (owned) => {
        this.owned.next(owned);
        this.updateOwnedStats(owned);
        this.loading = false;
      },
      error: (error) => {
        console.error('Error fetching owned:', error);
        this.owned.next([]);
        this.updateOwnedStats([]);
        this.loading = false;
      },
    });
  }

  private updateOwnedStats(owned: Punk[]): void {
    const wrapped = owned.filter((punk) => punk.wrapped);
    this.ownedCount = owned.length;
    this.wrappedCount = wrapped.length;
    this.unwrappedCount = owned.length - wrapped.length;
    this.wrappedPunkIds = wrapped.map((punk) => Number(punk.id));
  }

  /**
   * Apply wrap status locally after a confirmed tx and notify other views (e.g. dashboard).
   * Avoids re-fetching the indexer immediately, which often still reports stale wrapped state.
   */
  private setPunksWrappedStatus(punkIds: Array<string | number>, wrapped: boolean): void {
    if (punkIds.length === 0) {
      return;
    }

    const idSet = new Set(punkIds.map((id) => String(id)));
    const owned = this.owned.getValue() ?? [];
    const updated = owned.map((punk) =>
      idSet.has(String(punk.id)) ? { ...punk, wrapped } : punk
    );

    this.owned.next(updated);
    this.updateOwnedStats(updated);

    if (this.current && idSet.has(String(this.current.id))) {
      this.current = { ...this.current, wrapped };
    }

    const wallet = this.stateSvc.getWalletAddress();
    if (!wallet) {
      return;
    }

    window.dispatchEvent(
      new CustomEvent('ownedWrapStatusChanged', {
        detail: {
          wallet: wallet.toLowerCase(),
          updates: [...idSet].map((punkId) => ({ punkId, wrapped })),
        },
      })
    );
  }

  async triggerSingleAction(): Promise<void> {
    if (!this.current || this.isActionBusy) {
      return;
    }

    if (this.current.wrapped) {
      await this.unwrapSingle(this.current);
      return;
    }

    if (this.wrapStep === 'none') {
      await this.prepareWrap(this.current);
    } else {
      await this.finalizeWrap(this.current);
    }
  }

  async unwrapAll(): Promise<void> {
    if (this.wrappedCount === 0 || this.isActionBusy) {
      return;
    }

    this.isActionBusy = true;
    this.isWrapperTransactionActive = true;

    try {
      const approved = await this.web3Svc.isUnwrapV1PunksApproved();
      if (!approved) {
        this.txTrackingSvc.initTransactionMessage(
          '<p>Please confirm approval for batch unwrapping in your wallet. This is a one-time step.</p>'
        );
        const approvalHash = await this.web3Svc.approveUnwrapV1Punks();
        if (!approvalHash) {
          throw new Error('Approval transaction hash not returned');
        }

        this.txTrackingSvc.setTransactionMessage(
          approvalHash,
          '<p>Your batch unwrap approval is being processed.</p>'
        );
        const approvalReceipt = await this.web3Svc.waitForTransaction(approvalHash);
        this.txTrackingSvc.setProcessTransactionMessage(
          approvalReceipt,
          '<p>Approval confirmed. Preparing batch unwrap...</p>'
        );
      }

      this.txTrackingSvc.initTransactionMessage(
        `<p>Please confirm the transaction to unwrap ${this.wrappedCount} wrapped punk(s) in your wallet.</p>`
      );
      const hash = await this.web3Svc.unwrapAllV1Punks(this.wrappedPunkIds);
      if (!hash) {
        throw new Error('Batch unwrap transaction hash not returned');
      }

      this.txTrackingSvc.setTransactionMessage(
        hash,
        `<p>Unwrapping ${this.wrappedCount} punk(s) on the Ethereum network.</p>`
      );
      const receipt = await this.web3Svc.waitForTransaction(hash);
      this.txTrackingSvc.setProcessTransactionMessage(
        receipt,
        `<p>${this.wrappedCount} punk(s) unwrapped successfully! They are back on the native V1 contract.</p>`
      );

      const unwrappedIds = [...this.wrappedPunkIds];
      this.current = null;
      this.wrapStep = 'none';
      this.setPunksWrappedStatus(unwrappedIds, false);

      setTimeout(() => {
        this.txTrackingSvc.closeTxModal();
        this.isWrapperTransactionActive = false;
      }, 2500);
    } catch (error) {
      console.error('[WrapperV1Component] Batch unwrap error:', error);
      this.txTrackingSvc.setErrorTransactionMessage(error, '<p>There was an error unwrapping your punks.</p>');
      setTimeout(() => {
        this.isWrapperTransactionActive = false;
      }, 3000);
    } finally {
      this.isActionBusy = false;
    }
  }

  private async prepareWrap(punk: Punk): Promise<void> {
    this.isActionBusy = true;
    this.isWrapperTransactionActive = true;

    try {
      this.txTrackingSvc.initTransactionMessage(
        '<p>Please confirm the first wrap transaction in your wallet. This allows the wrapper contract to access your punk.</p>'
      );
      const hash = await this.web3Svc.prepareV1PunkWrapping(punk.id);
      if (!hash) {
        throw new Error('Wrap preparation transaction hash not returned');
      }

      this.txTrackingSvc.setTransactionMessage(hash, '<p>Your wrap preparation transaction is being processed.</p>');
      const receipt = await this.web3Svc.waitForTransaction(hash);
      this.txTrackingSvc.setProcessTransactionMessage(
        receipt,
        '<p>Step 1 complete. Click "Finalize wrap (2/2)" to finish wrapping.</p>'
      );

      this.wrapStep = 'sentToWrapContract';

      setTimeout(() => {
        this.txTrackingSvc.closeTxModal();
        this.isWrapperTransactionActive = false;
      }, 2500);
    } catch (error) {
      console.error('[WrapperV1Component] Wrap preparation error:', error);
      this.txTrackingSvc.setErrorTransactionMessage(error, '<p>There was an error preparing the wrap.</p>');
      this.wrapStep = 'none';
      setTimeout(() => {
        this.isWrapperTransactionActive = false;
      }, 3000);
    } finally {
      this.isActionBusy = false;
    }
  }

  private async finalizeWrap(punk: Punk): Promise<void> {
    this.isActionBusy = true;
    this.isWrapperTransactionActive = true;

    try {
      this.txTrackingSvc.initTransactionMessage(
        '<p>Please confirm the final wrap transaction in your wallet.</p>'
      );
      const hash = await this.web3Svc.wrapV1Punk(punk.id);
      if (!hash) {
        throw new Error('Wrap transaction hash not returned');
      }

      this.txTrackingSvc.setTransactionMessage(hash, '<p>Your wrap transaction is being processed.</p>');
      const receipt = await this.web3Svc.waitForTransaction(hash);
      this.txTrackingSvc.setProcessTransactionMessage(
        receipt,
        `<p>Punk #${punk.id} wrapped successfully! It is now tradeable on ERC-721 marketplaces.</p>`
      );

      this.wrapStep = 'none';
      this.setPunksWrappedStatus([punk.id], true);

      setTimeout(() => {
        this.txTrackingSvc.closeTxModal();
        this.isWrapperTransactionActive = false;
      }, 2500);
    } catch (error) {
      console.error('[WrapperV1Component] Wrap finalization error:', error);
      this.txTrackingSvc.setErrorTransactionMessage(error, '<p>There was an error wrapping your punk.</p>');
      setTimeout(() => {
        this.isWrapperTransactionActive = false;
      }, 3000);
    } finally {
      this.isActionBusy = false;
    }
  }

  private async unwrapSingle(punk: Punk): Promise<void> {
    this.isActionBusy = true;
    this.isWrapperTransactionActive = true;

    try {
      this.txTrackingSvc.initTransactionMessage(
        '<p>Please confirm the unwrap transaction in your wallet.</p>'
      );
      const hash = await this.web3Svc.unwrapPunk(punk.id);
      if (!hash) {
        throw new Error('Unwrap transaction hash not returned');
      }

      this.txTrackingSvc.setTransactionMessage(hash, '<p>Your unwrap transaction is being processed.</p>');
      const receipt = await this.web3Svc.waitForTransaction(hash);
      this.txTrackingSvc.setProcessTransactionMessage(
        receipt,
        `<p>Punk #${punk.id} unwrapped successfully! It is back on the native V1 contract.</p>`
      );

      this.current = null;
      this.wrapStep = 'none';
      this.setPunksWrappedStatus([punk.id], false);

      setTimeout(() => {
        this.txTrackingSvc.closeTxModal();
        this.isWrapperTransactionActive = false;
      }, 2500);
    } catch (error) {
      console.error('[WrapperV1Component] Unwrap error:', error);
      this.txTrackingSvc.setErrorTransactionMessage(error, '<p>There was an error unwrapping your punk.</p>');
      setTimeout(() => {
        this.isWrapperTransactionActive = false;
      }, 3000);
    } finally {
      this.isActionBusy = false;
    }
  }

  closeTransactionModal(): void {
    this.txTrackingSvc.closeTxModal();
    this.isWrapperTransactionActive = false;
  }

  trackByPunkId(_index: number, punk: Punk): string {
    return punk.id;
  }
}
